import ShareStatementsButton, { ShareEntityButtonStyles } from "@bToC/Components/ShareStatementsButton";
import downloadStatement from "@bToC/Handlers/DownloadStatementExcel";
import { StatementsPageContext } from "@bToC/Pages/StatementsPage";
import { StatementDataViewContext } from "@bToC/Store/Data/Statements/StatementSelector";
import { CustomState } from "@bToC/Store/Reducers";
import { ExtendedWidgetModule } from "@bToC/Types/ExtendedWidgetDefinition";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { get } from "@insite/client-framework/Services/ApiService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { productPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ShareEntityButton from "@insite/content-library/Components/ShareEntityButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    buttonsOrder = "buttonsOrder",
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

const enum buttons {
    print = "Print",
    email = "Email",
    excel = "Excel",
}

interface ButtonMapper {
    [key: string]: {
        button: any;
        clickable: any;
        showButtonOnTablet?: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.buttonsOrder]: ButtonModel[];
        [fields.generateEmailAttachmentFromWebpage]: boolean;
    };
}

interface ButtonModel {
    fields: {
        name: string;
    };
}

const mapStateToProps = (state: CustomState) => ({
    getStatementsParameter: state.customPagesReducer.statements.getStatementsParameter,
});

const mapDispatchToProps = {
    downloadStatement,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface StatementButtonSetStyles {
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    buttonsWrapper?: InjectableCss;
    buttonHidden?: HiddenProps;
    buttonGridContainer?: GridContainerProps;
    tabletButtonHidden?: HiddenProps;
    tabletMenuHidden?: HiddenProps;
    menuHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    printButton?: ButtonPresentationProps;
    printClickable?: ClickablePresentationProps;
    buttonWrapper?: InjectableCss;
}

export const statementButtonSetStyles: StatementButtonSetStyles = {
    shareEntityButtonStyles: {
        button: {
            buttonType: "outline",
            variant: "secondary",
        },
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            clear: both;
        `,
    },
    buttonHidden: {
        below: "lg",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    tabletButtonHidden: {
        below: "md",
        above: "md",
    },
    tabletMenuHidden: {
        below: "md",
        above: "md",
    },
    menuHidden: {
        above: "sm",
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
        `,
    },
    buttonWrapper: {
        css: css`
            margin-left: 20px;
        `,
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
};

export const emailButtonStyles = statementButtonSetStyles;
const styles = statementButtonSetStyles;

const StatementButtonSet: React.FunctionComponent<Props> = ({
    fields,
    getStatementsParameter,
    downloadStatement,
}: Props) => {
    const toasterContext = React.useContext(ToasterContext);
    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;
    const onClickPrint = () => {
        openPrintDialog();
    };

    const onClickEmail = () => {
        toasterContext.addToast({
            body: <div>{translate("This has yet to be implemented")}</div>,
            messageType: "success",
        });
    };

    const onExcelClick = () => {
        const statementExcelError = translate("Unable to generate statement Excel document.");
        downloadStatement({
            getStatementsParameter,
            onUnauthenticated: () => {
                toasterContext.addToast({
                    body: `${translate("Login Required.")}`,
                    messageType: "warning",
                });
            },
            onBeginDownload: () => {
                toasterContext.addToast({
                    body: `${translate("Generating statement Excel document. It will be downloaded once complete.")}`,
                    messageType: "info",
                    timeoutLength: 5000,
                });
            },
            onError: () => {
                toasterContext.addToast({
                    body: statementExcelError,
                    messageType: "warning",
                    timeoutLength: 5000,
                });
            },
        });
    };

    const buttonList: ButtonMapper = {};

    buttonList[buttons.excel] = {
        button: (
            // GE-215
            <Button onClick={onExcelClick}>{translate("Excel")}</Button>
        ),
        clickable: (
            // GE-215
            <Clickable onClick={onExcelClick}>{translate("Excel")}</Clickable>
        ),
    };

    buttonList[buttons.email] = {
        button: (
            // GE-216
            <ShareStatementsButton
                entityId="0"
                entityName="Statement"
                variant="button"
                extendedStyles={styles.shareEntityButtonStyles}
                generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
            />
        ),
        clickable: (
            // GE-216
            <ShareStatementsButton
                entityId="0"
                entityName="Statement"
                variant="clickable"
                extendedStyles={styles.shareEntityButtonStyles}
                generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
            />
        ),
    };

    buttonList[buttons.print] = {
        button: (
            <Button {...styles.printButton} onClick={onClickPrint}>
                {translate("Print")}
            </Button>
        ),
        clickable: (
            <Clickable {...styles.printClickable} onClick={onClickPrint}>
                {translate("Print")}
            </Clickable>
        ),
    };

    const buttonsToRender = fields.buttonsOrder.map(button => buttonList[button.fields.name]).filter(button => button);

    return (
        <StyledWrapper {...styles.buttonsWrapper}>
            <Hidden {...styles.buttonHidden}>
                {buttonsToRender.map((button, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledWrapper {...styles.buttonWrapper} key={index}>
                        {button.button}
                    </StyledWrapper>
                ))}
            </Hidden>
            <Hidden {...styles.tabletButtonHidden}>
                {buttonsToRender
                    .filter(o => o.showButtonOnTablet)
                    .map((button, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <StyledWrapper {...styles.buttonWrapper} key={index}>
                            {button.button}
                        </StyledWrapper>
                    ))}
            </Hidden>
            <Hidden {...styles.tabletMenuHidden}>
                <OverflowMenu hasChildPortal position="end" {...styles.overflowMenu}>
                    {buttonsToRender
                        .filter(o => !o.showButtonOnTablet)
                        .map((button, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={index}>{button.clickable}</div>
                        ))}
                </OverflowMenu>
            </Hidden>
            <Hidden {...styles.menuHidden}>
                <OverflowMenu hasChildPortal position="end" {...styles.overflowMenu}>
                    {buttonsToRender.map((button, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={index}>{button.clickable}</div>
                    ))}
                </OverflowMenu>
            </Hidden>
        </StyledWrapper>
    );
};

const widgetModule: ExtendedWidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(StatementButtonSet)),
    definition: {
        allowedContexts: ["StatementsPageContext"],
        displayName: "Statements Button Set",
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.buttonsOrder,
                displayName: "Buttons Statement",
                editorTemplate: "ListField",
                defaultValue: [
                    { fields: { name: buttons.excel } },
                    { fields: { name: buttons.print } },
                    { fields: { name: buttons.email } },
                ],
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                hideAdd: true,
                hideEdit: true,
                hideDelete: true,
                fieldType: "General",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                displayName: "Generate Email Attachment From Webpage",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the statement list generates an email attachment using the Statement List page, rather than a pre-defined PDF file.",
            },
        ],
    },
};

export default widgetModule;
